/**
 * Does this resolved value actually drive?
 *
 * Every consumer of a bound field has to answer that, and answering it as "is
 * this truthy" is wrong in a way that is hard to see. A binding nothing
 * declares survives resolution **as its own source text** —
 * `TraceBehavior._resolveValue` returns the string when `resolveProperty` comes
 * back undefined — so a field bound to `structureColor` holds the literal
 * `"$structureColor"` until something declares that property. It is non-empty,
 * so a truthiness gate reads it as a value and hands it to whatever parses the
 * field.
 *
 * For colors that is actively destructive: neither `Color3.FromHexString` nor
 * `Color4.FromHexString` rejects bad input — they answer black and transparent
 * black respectively. An object whose style stopped declaring a role therefore
 * does not render "unstyled", it renders *gone*.
 *
 * Three inputs mean "nothing drives this field", and all three must land on the
 * same answer — restore what the consumer authored:
 *
 *  - an **unresolved binding**: no provider declares the property;
 *  - `''`: the explicit "no override" value;
 *  - a **malformed hex**, for the color helpers.
 */

import { isPropertyBinding } from './tracebehavior';
import { Logger } from '../utils/Logger';

/** Accepted hex color formats across behaviors: `#RRGGBB` or `#RRGGBBAA`. */
export const HEX_COLOR_RE = /^#[0-9A-Fa-f]{6}([0-9A-Fa-f]{2})?$/;

/**
 * Whether a resolved string field drives: present, non-empty, and not a
 * binding that failed to resolve.
 */
export function drivesValue(value: unknown): value is string {
    return typeof value === 'string' && value !== '' && !isPropertyBinding(value);
}

/**
 * The hex a resolved color field drives with, or `null` when it drives nothing.
 *
 * `label` names the field in the warning — an invalid *resolved* color is worth
 * saying out loud (a style set it, or a state override did), while an
 * unresolved binding and `''` are ordinary states and stay quiet.
 */
export function drivingHexColor(value: unknown, label: string): string | null {
    if (!drivesValue(value)) return null;
    if (!HEX_COLOR_RE.test(value)) {
        Logger.warn(`[${label}] Ignoring invalid color "${value}" — expected #RRGGBB or #RRGGBBAA hex`);
        return null;
    }
    return value;
}
